import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { AlertCircle, CalendarDays } from 'lucide-react';
import { ReportHeader } from '../components/ReportHeader';
import { Sparkline } from '../components/Sparkline';
import { trailingMonths } from '../lib/snapshotRanges';
import EventSessionsDrawer from '../components/EventSessionsDrawer';

const API = process.env.REACT_APP_BACKEND_URL;

const UNAVAILABLE_MSG =
  'Event attendance data is temporarily unavailable. This can happen if your account is not connected to the upstream LevelUp API. Please try again shortly or contact your administrator.';

const fmt = (n) => (n ?? 0).toLocaleString();
const fmtDT = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '—');
const fmtDur = (m) => (m == null ? '—' : `${Math.round(m)} min`);

function SABadge({ name }) {
  if (!name) return <span style={{ color: '#94a3b8' }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#4338ca', whiteSpace: 'nowrap' }}>
      {name}
    </span>
  );
}

function PaginationBar({ page, totalPages, total, perPage, onPage, testId }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return (
    <div className="pagination" data-testid={testId}>
      <span className="pagination-info">{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

const ErrorBanner = ({ message }) => (
  <div data-testid="events-error" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '24px 20px' }}>
    <AlertCircle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
    <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{message}</p>
  </div>
);

const Spinner = () => (
  <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
);

// ── Executive snapshot (Total Attendances + last-6-months trend) ────────────────
function SnapshotBar({ dateFrom, dateTo }) {
  const [snap, setSnap] = useState(null);
  const [series, setSeries] = useState(null);
  const reqId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const myId = ++reqId.current;
    (async () => {
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const r = await axios.get(`${API}/api/lup/v2/report/event-attendances/insights`, { params, withCredentials: true });
        if (cancelled || myId !== reqId.current) return;
        setSnap(r.data || null);
      } catch (e) {
        if (!cancelled && myId === reqId.current) setSnap(null);
      }
    })();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const months = trailingMonths(6);
        const res = await Promise.all(months.map(m =>
          axios.get(`${API}/api/lup/v2/report/event-attendances`, { params: { page: 1, per_page: 1, date_from: m.from, date_to: m.to }, withCredentials: true })
        ));
        if (cancelled) return;
        setSeries(res.map(r => r.data?.total || 0));
      } catch (e) {
        if (!cancelled) setSeries(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const BigTile = ({ k, label, value, sub, spark }) => (
    <div className="stat-card" data-testid={`events-snapshot-${k}`} style={{ padding: 16, minWidth: 0 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', fontWeight: 600 }}>{label}</div>
      <div className="stat-value" style={{ fontSize: value && value.length > 14 ? 16 : 22, fontWeight: 800, color: '#0f172a', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>{value}</div>
      {sub != null && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{sub}</div>}
      {spark && <Sparkline values={spark} />}
    </div>
  );

  return (
    <div data-testid="events-snapshot-bar" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#94a3b8', fontWeight: 700 }}>EventsUp Snapshot</div>
        {series && <div style={{ fontSize: 11, color: '#94a3b8' }}>Trend: last 6 months</div>}
      </div>
      {snap == null ? (
        <div className="stat-card" style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>Snapshot data unavailable for the selected range.</div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12 }}>
        <BigTile k="total" label="Total Attendances" value={fmt(snap.total_attendances)} sub="attended sessions" spark={series} />
        <BigTile k="events" label="Events" value={fmt(snap.unique_events)} sub="with attendance" />
        <BigTile k="top-event" label="Top Event" value={snap.top_event?.event_title || '—'} sub={snap.top_event ? `${fmt(snap.top_event.attendances)} attendances` : null} />
        <BigTile k="avg-duration" label="Avg Duration" value={`${fmt(snap.avg_duration_minutes)} min`} sub="per attended session" />
      </div>
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────────
export default function EventsUpActivity() {
  const [period, setPeriod] = useState({ startDate: '', endDate: '' });
  const [searchInput, setSearchInput] = useState('');
  const [applied, setApplied] = useState({ search: '', sort: 'enrolled_at:desc' });
  const [sort, setSort] = useState('enrolled_at:desc');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [piiAllowed, setPiiAllowed] = useState(false);
  const [drawerEvent, setDrawerEvent] = useState(null);
  const closeDrawer = useCallback(() => setDrawerEvent(null), []);
  const PER_PAGE = 100;

  const handlePeriod = useCallback(({ startDate, endDate }) => setPeriod({ startDate: startDate || '', endDate: endDate || '' }), []);

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setPiiAllowed(r.data?.pii_allowed === true))
      .catch(() => setPiiAllowed(false));
  }, []);

  const fetchData = useCallback(async (p, f) => {
    setLoading(true); setErrorMsg(null);
    try {
      const params = { page: p, per_page: PER_PAGE, sort: f.sort };
      if (f.search) params.search = f.search;
      if (period.startDate) params.date_from = period.startDate;
      if (period.endDate) params.date_to = period.endDate;
      const r = await axios.get(`${API}/api/lup/v2/report/event-attendances`, { params, withCredentials: true });
      setRows(r.data?.data || []);
      setMeta({ total: r.data?.total || 0, last_page: r.data?.last_page || 1 });
      setPage(r.data?.current_page || p);
    } catch (e) {
      setErrorMsg(UNAVAILABLE_MSG); setRows([]); setMeta({ total: 0, last_page: 1 });
    } finally { setLoading(false); }
  }, [period.startDate, period.endDate]);

  useEffect(() => { fetchData(1, applied); }, [fetchData, applied]);

  const apply = () => setApplied({ search: searchInput, sort });
  const reset = () => { setSearchInput(''); setSort('enrolled_at:desc'); setApplied({ search: '', sort: 'enrolled_at:desc' }); };

  return (
    <div data-testid="eventsup-activity-page">
      <ReportHeader reportTitle="EventsUp" onChange={handlePeriod} />
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CalendarDays size={22} color="#4338ca" /> EventsUp Activity
        </h1>
        <p>Event session attendances — one row per attendee per attended session — sourced live from the LevelUp reporting API.</p>
      </div>

      <SnapshotBar dateFrom={period.startDate} dateTo={period.endDate} />

      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input type="text" className="filter-input" placeholder="Event, session, attendee, email, MS ID…"
            value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} data-testid="events-search" />
        </div>
        <div className="filter-group">
          <label className="filter-label">Sort</label>
          <select className="filter-select" value={sort} onChange={e => setSort(e.target.value)} data-testid="events-sort">
            <option value="enrolled_at:desc">Enrolled (newest)</option>
            <option value="enrolled_at:asc">Enrolled (oldest)</option>
            <option value="completed_at:desc">Completed (newest)</option>
            <option value="completed_at:asc">Completed (oldest)</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={apply} data-testid="events-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={reset} data-testid="events-reset-btn">Reset</button>
        </div>
      </div>

      <div className="table-container">
        {loading ? <Spinner /> : errorMsg ? <ErrorBanner message={errorMsg} /> : rows.length === 0 ? (
          <div className="empty-state"><h3>No attendances found</h3><p>Try adjusting your filters.</p></div>
        ) : (
          <>
            <div className="table-header">
              <span className="table-title">Event Attendances</span>
              <span className="table-count" style={{ marginLeft: 8 }}>({fmt(meta.total)} rows)</span>
            </div>
            <div className="table-scroll">
              <table data-testid="events-table">
                <thead>
                  <tr>
                    <th>Event</th><th>Session</th>
                    {piiAllowed && <th>Attendee</th>}
                    {piiAllowed && <th>Email</th>}
                    <th>Company</th><th>Country</th><th>Region</th>
                    <th>MS Course ID</th><th>Solution Area</th><th>Solution Play</th>
                    <th>Enrolled</th><th>Completed</th><th className="td-right">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.event_id}-${row.session_id}-${i}`} data-testid={`events-row-${i}`}>
                      <td style={{ fontSize: 13 }}>
                        {row.event_id != null ? (
                          <button onClick={() => setDrawerEvent({ id: row.event_id, title: row.event_title })} data-testid={`events-event-link-${i}`}
                            title="View sessions and attendees"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4338ca', fontWeight: 600, fontSize: 13, textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                            {row.event_title || row.event_id}
                          </button>
                        ) : <div style={{ fontWeight: 500 }}>{row.event_title || '—'}</div>}
                        <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{row.event_id}</div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        <div>{row.session_title || '—'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{row.session_id}</div>
                      </td>
                      {piiAllowed && <td style={{ fontSize: 13 }}>{row.learner_name || '—'}</td>}
                      {piiAllowed && <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#3182ce' }}>{row.email || '—'}</td>}
                      <td style={{ fontSize: 12 }}>{row.company || '—'}</td>
                      <td style={{ fontSize: 12 }}>{row.country || '—'}</td>
                      <td style={{ fontSize: 12 }}>{row.region || '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.ms_course_id || '—'}</td>
                      <td><SABadge name={row.solution_area} /></td>
                      <td style={{ fontSize: 12 }}>{row.solution_play || '—'}</td>
                      <td style={{ fontSize: 12 }}>{fmtDT(row.enrolled_at)}</td>
                      <td style={{ fontSize: 12 }}>{fmtDT(row.completed_at)}</td>
                      <td className="td-right" style={{ fontWeight: 600 }}>{fmtDur(row.duration_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={meta.last_page} total={meta.total} perPage={PER_PAGE} onPage={p => fetchData(p, applied)} testId="events-pagination" />
          </>
        )}
      </div>
      {drawerEvent && <EventSessionsDrawer event={drawerEvent} pii={piiAllowed} onClose={closeDrawer} />}
    </div>
  );
}
